import { useMemo } from 'react';

import { Route, Routes } from 'react-router-dom';

import { ToastNotification } from '@/components/ToastNotification';
import { usePermissions } from '@/hooks/usePermissions';
import CourseEdit from '@/Pages/Courses/CourseEdit';
import { CourseEditProvider } from '@/Pages/Courses/CourseEditContext';
import Settings from '@/Pages/Settings/Settings';
import { GlobalAdminStyle } from '@/Shared/StyledElements';

import {
	CourseAlignment,
	Courses,
	Disciplines,
	Divisions,
	EditTags,
	NotFound,
	Privacy,
	Reports,
	Stats,
	Submissions,
	Users,
} from '../Pages/index';
import CourseMapD3React from './CourseMap/CourseMapD3React';

interface PlatformRoute {
	path: string;
	element: React.ReactNode;
	children?: PlatformRoute[];
}

const Panel = () => {
	const {
		isLoading,
		userRole,
		canManageUsers,
		canManageTags,
		canManageDisciplines,
		canManageDivisions,
		canViewStats,
		canManageSettings,
		canViewCourseMap,
	} = usePermissions();

	const routes = useMemo(() => {
		// Base routes available to all authenticated users
		const baseRoutes: PlatformRoute[] = [
			{
				path: 'courses/',
				element: <Courses />,
			},
			{
				path: 'courses/:courseId',
				element: (
					<CourseEditProvider>
						<CourseEdit />
					</CourseEditProvider>
				),
			},
			{
				path: '/submissions',
				element: <Submissions />,
			},
			{
				path: '/submissions/:courseId',
				element: (
					<CourseEditProvider>
						<CourseEdit />
					</CourseEditProvider>
				),
			},
			{
				path: '/course-alignment',
				element: <CourseAlignment />,
			},
			{
				path: '/privacy-policy',
				element: <Privacy />,
			},
		];

		// Admin-only routes
		const adminRoutes: PlatformRoute[] = [];

		if (canManageUsers) {
			adminRoutes.push({
				path: '/users',
				element: <Users />,
			});
		}

		if (canManageTags) {
			adminRoutes.push({
				path: '/tags',
				element: <EditTags />,
			});
		}

		if (canManageDisciplines) {
			adminRoutes.push({
				path: '/disciplines',
				element: <Disciplines />,
			});
		}

		if (canManageDivisions) {
			adminRoutes.push({
				path: '/divisions',
				element: <Divisions />,
			});
		}

		if (canViewStats) {
			adminRoutes.push({
				path: '/stats',
				element: <Stats />,
			});
			adminRoutes.push({
				path: '/reports',
				element: <Reports />,
			});
		}

		if (canViewCourseMap) {
			adminRoutes.push({
				path: '/course-map',
				element: <CourseMapD3React />,
			});
		}

		if (canManageSettings) {
			adminRoutes.push({
				path: '/settings',
				element: <Settings />,
			});
		}

		return [...baseRoutes, ...adminRoutes];
	}, [
		canManageUsers,
		canManageTags,
		canManageDisciplines,
		canManageDivisions,
		canViewStats,
		canManageSettings,
		canViewCourseMap,
	]);

	if (isLoading || userRole === undefined) {
		return null;
	}

	return (
		<>
			<GlobalAdminStyle />
			<div
				id="panel"
				style={{ gridArea: 'panel' }}
				className="bg-white rounded-md shadow-[0px_1px_0px_rgba(0,15,25,0.05),0px_8px_16px_rgba(0,15,25,0.05)] pt-4 pr-4 px-4 small-screen:px-6 py-3 max-w-[1500px] overflow-x-hidden overflow-y-auto h-full min-h-0 relative"
			>
				<Routes>
					{routes.map((route) => (
						<Route key={route.path} path={route.path} element={route.element} />
					))}
					<Route path="*" element={<NotFound />} />
				</Routes>
				<ToastNotification />
			</div>
		</>
	);
};

export default Panel;
